// ReviewStep component for final review and confirmation
// Displays summary of all entered data before submission
// Based on product-merchant-crud.html review section

import React from "react";
import { WizardStepComponentProps } from "src/shared/forms/types/WizardTypes";
import { BasicInfoData } from "./BasicInfoStep";
import { ClassificationData } from "./ClassificationStep";
import { SalesInfoData } from "./SalesInfoStep";
import { ShippingData } from "./ShippingStep";

// Utility function for class names
const classNames = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(" ");
};

export interface ReviewData {
  // This step doesn't have its own data, it reviews all other steps
  // No additional fields needed for review-only step
  publishImmediately?: boolean;
}

interface ReviewStepProps extends WizardStepComponentProps {
  onEditStep?: (step: number) => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  data,
  onDataChange,
  allData,
  errors = [],
  onEditStep,
}) => {
  const reviewData = data as ReviewData;
  const basicInfo = allData?.basicInfo as BasicInfoData;
  const classification = allData?.classification as ClassificationData;
  const salesInfo = allData?.salesInfo as SalesInfoData;
  const shipping = allData?.shipping as ShippingData;

  // No field changes needed for review-only step

  // Handle navigation to specific step (will be handled by parent wizard)
  const handleEdit = (stepNumber: number) => {
    if (onEditStep) {
      onEditStep(stepNumber);
    } else {
      console.log(`Navigate to step ${stepNumber}`);
    }
  };

  return (
    <div className="tw-bg-white dark:tw-bg-[#1C252E] tw-rounded-xl tw-border tw-border-gray-200 dark:tw-border-[#233648] tw-p-4 md:tw-p-6 tw-shadow-sm">
      <div className="tw-mb-6 tw-border-b tw-border-gray-200 dark:tw-border-[#233648] tw-pb-4">
        <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 dark:tw-text-white">
          Review & Publish
        </h3>
        <p className="tw-text-sm tw-text-gray-500 dark:tw-text-[#92adc9]">
          Review your product information before publishing to the marketplace.
        </p>
      </div>

      <div className="tw-grid tw-gap-6">
        {/* Basic Information Summary */}
        <div className="tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h4 className="tw-font-semibold tw-text-gray-900 dark:tw-text-white">
              Basic Information
            </h4>
            <button
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => handleEdit(1)}
            >
              Edit
            </button>
          </div>

          {/* Product Identifier Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Product SKU:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {basicInfo?.productSku || "Not set"}
              </span>
            </div>
          </div>

          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>

          {/* Product Details Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Product Name:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {basicInfo?.productName || "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Product Description:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {basicInfo?.productDescription || "Not set"}
              </span>
            </div>
          </div>

          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>

          {/* Product Media Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Product Images:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {basicInfo?.productImages?.length || 0} images
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Product Video URL:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {basicInfo?.videoUrl ? "Provided" : "Not set"}
              </span>
            </div>
          </div>
        </div>

        {/* Classification Summary */}
        <div className="tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h4 className="tw-font-semibold tw-text-gray-900 dark:tw-text-white">
              Classification
            </h4>
            <button
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => handleEdit(2)}
            >
              Edit
            </button>
          </div>

          {/* Category & Brand Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Category:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {classification?.categoryPath && classification.categoryPath.length > 0
                  ? classification.categoryPath
                      .map((c) => c.name || c.id)
                      .join(" > ")
                  : "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Brand:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {classification?.brandName || "Not set"}
              </span>
            </div>
          </div>

          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>

          {/* License Information Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                License Number:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {classification?.licenseNumber || "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                License File:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {classification?.licenseFile ? "Uploaded" : "Not set"}
              </span>
            </div>
          </div>

          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>

          {/* Product Specifications Section - Dynamic Attributes */}
          {classification?.categoryAttributes && Object.keys(classification.categoryAttributes).length > 0 && (
            <div className="tw-space-y-2">
              <div className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
                Product Specifications:
              </div>
              {Object.entries(classification.categoryAttributes).map(([key, value]) => (
                <div key={key} className="tw-text-sm">
                  <span className="tw-text-gray-500 dark:tw-text-gray-400">
                    {key}:
                  </span>
                  <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                    {value || "Not set"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sales Information Summary */}
        <div className="tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h4 className="tw-font-semibold tw-text-gray-900 dark:tw-text-white">
              Sales Information
            </h4>
            <button
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => handleEdit(3)}
            >
              Edit
            </button>
          </div>
          {/* Order Settings Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Minimum Purchase:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.minOrder || "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Product Variants:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.hasVariants ? "Yes" : "No"}
              </span>
            </div>
          </div>

          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>

          {/* Variants Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
              Product Variants:
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Variant Options:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.variantOptions?.length || 0} options
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Variant Combinations:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.variantCombinations?.length || 0} combinations
              </span>
            </div>
          </div>
          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>
          {/* Pricing Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Currency:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.currency || "IDR"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Regular Price:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.productPrice
                  ? `${salesInfo.currency || "IDR"} ${parseInt(
                      salesInfo.productPrice.toString()
                    ).toLocaleString("id-ID")}`
                  : "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Sale Price:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.salePrice
                  ? `${salesInfo.currency || "IDR"} ${parseInt(
                      salesInfo.salePrice.toString()
                    ).toLocaleString("id-ID")}`
                  : "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Cost Price:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.costPrice
                  ? `${salesInfo.currency || "IDR"} ${parseInt(
                      salesInfo.costPrice.toString()
                    ).toLocaleString("id-ID")}`
                  : "Not set"}
              </span>
            </div>
          </div>
          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>
          {/* Inventory Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Stock Quantity:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.totalStock || "Not set"}
              </span>
            </div>
          </div>
          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>
          {/* Tags Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Product Tags:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.tags?.length
                  ? salesInfo.tags.join(", ")
                  : "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Promotion Tags:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {salesInfo?.promotionTags?.length
                  ? salesInfo.promotionTags.join(", ")
                  : "Not set"}
              </span>
            </div>
          </div>
        </div>

        {/* Shipping Information Summary */}
        <div className="tw-border tw-border-gray-200 dark:tw-border-[#364152] tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
            <h4 className="tw-font-semibold tw-text-gray-900 dark:tw-text-white">
              Shipping Information
            </h4>
            <button
              className="tw-text-primary hover:tw-underline tw-text-sm"
              onClick={() => handleEdit(4)}
            >
              Edit
            </button>
          </div>
          {/* Package Dimensions & Weight Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Package Weight:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {shipping?.packageDimensions?.weight
                  ? `${shipping.packageDimensions.weight} kg`
                  : "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Package Dimensions:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {shipping?.packageDimensions?.length &&
                shipping?.packageDimensions?.width &&
                shipping?.packageDimensions?.height
                  ? `${shipping.packageDimensions.length}x${shipping.packageDimensions.width}x${shipping.packageDimensions.height} cm`
                  : "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Is Dangerous Item?:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {shipping?.isDangerous ? "Yes" : "No"}
              </span>
            </div>
          </div>
          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>
          {/* Packing Instructions Section */}
          <div className="tw-text-sm">
            <span className="tw-text-gray-500 dark:tw-text-gray-400">
              Packing Instructions:
            </span>
            <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
              {shipping?.packingInstructions || "Not set"}
            </span>
          </div>
          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>
          {/* Shipping Companies & Services Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm tw-font-medium tw-text-gray-700 dark:tw-text-gray-300 tw-mb-2">
              Shipping Companies & Services:
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Enabled Companies:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {shipping?.shippingCompanies?.filter((c) => c.enabled).length ||
                  0}{" "}
                companies
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Enabled Services:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {shipping?.shippingCompanies?.reduce(
                  (total, company) =>
                    total +
                    (company.services?.filter((s) => s.enabled).length || 0),
                  0
                ) || 0}{" "}
                services
              </span>
            </div>
          </div>
          {/* Border Separator */}
          <div className="tw-my-4 tw-border-t tw-border-gray-200 dark:tw-border-[#364152]"></div>
          {/* Pre-Order Settings Section */}
          <div className="tw-space-y-2">
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Enable Pre-Order:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {shipping?.preOrderSettings?.enabled ? "Yes" : "No"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Pre-Order Processing Time:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {shipping?.preOrderSettings?.processingDays
                  ? `${shipping.preOrderSettings.processingDays} days`
                  : "Not set"}
              </span>
            </div>
            <div className="tw-text-sm">
              <span className="tw-text-gray-500 dark:tw-text-gray-400">
                Pre-Order Type:
              </span>
              <span className="tw-text-gray-900 dark:tw-text-white tw-font-medium tw-ml-2">
                {shipping?.preOrderSettings?.type === 'PRE_ORDER' && 'Pre-Order'}
                {shipping?.preOrderSettings?.type === 'MADE_TO_ORDER' && 'Made to Order'}
                {shipping?.preOrderSettings?.type === 'CUSTOM' && 'Custom'}
                {!shipping?.preOrderSettings?.type && 'Pre-Order'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Step Errors Display */}
      {errors.length > 0 && (
        <div className="tw-mt-6 tw-bg-red-50 dark:tw-bg-red-900/20 tw-border tw-border-red-200 dark:tw-border-red-700 tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-items-start tw-gap-3">
            <span className="material-symbols-outlined tw-text-red-600 dark:tw-text-red-400 tw-mt-0.5">
              error
            </span>
            <div>
              <h4 className="tw-font-medium tw-text-red-800 dark:tw-text-red-300 tw-mb-2">
                Please correct the following errors before submitting:
              </h4>
              <ul className="tw-text-sm tw-text-red-700 dark:tw-text-red-400 tw-space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewStep;
